import Box from '@mui/material/Box';
import { useMemo, useState, memo } from 'react';
import { useServerTime } from 'src/contexts/useServerTime';
import { CryptoChain } from 'src/web3/CryptoChain';
import { ExpandableButton, ExpandableButtonProps } from './ExpandableButton';
import Tooltip from '@mui/material/Tooltip';

export type ExpandableButtonsProps = {
  lowestAskFormatted?: string;
  highestBidFormatted?: string;
  priceHourlyFormatted?: string;
  contractAddress: string;
  itemId: string;
  chainId: number;
  rentable?: boolean;
  rentedUntil?: Date;
  isOwner?: boolean;
  showAsWhitelisted: boolean;
  isWhitelisted?: boolean;
  hasRentOffer: boolean;
  hasBidOffer?: boolean;
  contractType: string | null;
  isItemRented: boolean;
};

export const ExpandableButtons = memo(function ExpandableButtonsUnmemoized({
  lowestAskFormatted,
  highestBidFormatted,
  priceHourlyFormatted,
  contractAddress,
  itemId,
  chainId,
  rentable = false,
  rentedUntil = new Date(0),
  isOwner = false,
  showAsWhitelisted,
  hasRentOffer,
  contractType,
  isItemRented,
}: ExpandableButtonsProps) {
  const { now } = useServerTime();
  const canBuy = lowestAskFormatted || isOwner;
  const canRent =
    (priceHourlyFormatted && rentedUntil.getTime() < now()) ||
    (isOwner && rentable && !hasRentOffer);
  const canRemove = isOwner && rentable && hasRentOffer && !isItemRented;
  const bidType = contractType === 'ERC721' ? 'BID' : 'ORDER';
  const canBid =
    !isOwner || (isOwner && (highestBidFormatted || bidType === 'ORDER'));
  const [expandedButtonIndex, setExpandedButtonIndex] = useState(
    canBuy || (canRent && !showAsWhitelisted) ? 1 : 0,
  );

  // TODO: have hrefs lead to operation start
  const buttons = useMemo(() => {
    const cryptoChain = CryptoChain.from(chainId);
    const baseNavigation = `/item/${cryptoChain.coin.toLowerCase()}/${contractAddress}/${itemId}`;
    const result: Omit<
      ExpandableButtonProps,
      'expanded' | 'showAsWhitelisted'
    >[] = [];
    if (canBid) {
      result.push({
        variant: 'bid',
        price: highestBidFormatted,
        textExpanded:
          (isOwner && bidType === 'ORDER') || !isOwner ? bidType : 'ACCEPT',
        href: `${baseNavigation}?operation=offer`,
      });
    }
    if (canBuy) {
      result.push({
        variant: 'buy',
        price: bidType === 'ORDER' ? undefined : lowestAskFormatted,
        textExpanded:
          isOwner && !lowestAskFormatted && bidType === 'BID'
            ? 'LIST FOR SALE'
            : bidType === 'ORDER'
            ? 'BUY / SELL'
            : isOwner && lowestAskFormatted
            ? 'SELL'
            : 'BUY',
        href: `${baseNavigation}?operation=fulfillment`,
      });
    }
    if (canRemove) {
      result.push({
        variant: 'remove',
        textExpanded: 'REMOVE LEND ORDER',
        href: `${baseNavigation}?operation=fulfillment&temp=temporary`,
      });
    }
    if (canRent && !canRemove) {
      result.push({
        variant: 'rent',
        price: priceHourlyFormatted,
        textExpanded: isOwner ? 'LEND ITEM' : 'RENT',
        href: `${baseNavigation}?operation=fulfillment&temp=temporary`,
      });
    }
    return result;
  }, [
    canBuy,
    canRent,
    canRemove,
    canBid,
    chainId,
    contractAddress,
    highestBidFormatted,
    itemId,
    lowestAskFormatted,
    priceHourlyFormatted,
    isOwner,
    bidType,
  ]);

  return (
    <>
      {buttons.map((button, index) => {
        const whitelistItem = button.variant === 'rent' && showAsWhitelisted;
        const tooltipTitle = whitelistItem
          ? 'Whitelist access is required to rent this item.'
          : undefined;
        return (
          <Tooltip title={tooltipTitle} key={button.variant}>
            <Box
              sx={{
                width: expandedButtonIndex === index ? '100%' : '25px',
                transition: '0.15s ease-out all',
              }}
              onMouseOver={() => {
                return whitelistItem
                  ? undefined
                  : setExpandedButtonIndex(index);
              }}
            >
              <ExpandableButton
                {...button}
                expanded={expandedButtonIndex === index}
                showAsWhitelisted={showAsWhitelisted}
              />
            </Box>
          </Tooltip>
        );
      })}
    </>
  );
});
